import { Component, ChangeDetectionStrategy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { CsvExportService } from '../../../../services/csv-export/csv-export.service'
import { TrygroupDashboardSettingsService } from '../../../../services/trygroup-dashboard-settings/trygroup-dashboard-settings.service'

type CsvRow = Record<string, string>

const MAX_EXPORT_DAYS = 93

@UntilDestroy()
@Component({
  selector: 'app-trygroup-access-log-export-settings-page',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
  ],
  template: `
    <section class="settings-panel">
      <div class="panel-header">
        <div>
          <h3>アクセスログエクスポート</h3>
          <p>ダッシュボード利用状況をユーザ×日付のCSVで出力します。</p>
        </div>
      </div>

      <div class="date-range">
        <label>
          開始日
          <input
            type="date"
            [(ngModel)]="startDate"
            (ngModelChange)="onDateChange()"
          />
        </label>
        <label>
          終了日
          <input
            type="date"
            [(ngModel)]="endDate"
            (ngModelChange)="onDateChange()"
          />
        </label>
      </div>

      @if (validationError) {
        <p class="error">{{ validationError }}</p>
      }

      <button
        mat-flat-button
        color="primary"
        type="button"
        [disabled]="isExporting || !!validationError"
        (click)="downloadAccessLog()"
      >
        <span class="button-content">
          @if (isExporting) {
            <mat-spinner diameter="18"></mat-spinner>
          } @else {
            <mat-icon>download</mat-icon>
          }
          {{ isExporting ? 'エクスポート中...' : 'CSVダウンロード' }}
        </span>
      </button>

      @if (errorMessage) {
        <p class="error">{{ errorMessage }}</p>
      }
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .settings-panel {
        padding: 20px;
        background: #ffffff;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        box-shadow: 0 8px 24px rgb(15 23 42 / 6%);
      }

      .panel-header {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        justify-content: space-between;
        margin-bottom: 18px;
      }

      h3 {
        margin: 0;
        font-size: 18px;
        font-weight: 800;
      }

      p {
        margin: 6px 0 0;
        color: #64748b;
        font-size: 13px;
      }

      .date-range {
        display: flex;
        gap: 16px;
        align-items: center;
        margin-bottom: 16px;
      }

      .date-range label {
        display: flex;
        flex-direction: column;
        gap: 4px;
        font-size: 13px;
        color: #374151;
      }

      .date-range input[type='date'] {
        padding: 6px 10px;
        border: 1px solid #d1d5db;
        border-radius: 6px;
        font-size: 14px;
      }

      .button-content {
        display: inline-flex;
        gap: 8px;
        align-items: center;
      }

      .button-content mat-icon {
        width: 18px;
        height: 18px;
        font-size: 18px;
      }

      .error {
        margin-top: 16px;
        color: #dc2626;
        font-weight: 700;
      }
    `,
  ],
})
export class TrygroupAccessLogExportSettingsPageComponent {
  startDate: string
  endDate: string
  isExporting = false
  validationError = ''
  errorMessage = ''

  constructor(
    private trygroupDashboardSettingsService: TrygroupDashboardSettingsService,
    private csvExportService: CsvExportService,
  ) {
    this.endDate = formatDateInput(new Date())
    const oneMonthAgo = new Date()
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1)
    this.startDate = formatDateInput(oneMonthAgo)
  }

  onDateChange(): void {
    this.validationError = validateDateRange(this.startDate, this.endDate)
  }

  /** アクセスログデータを取得し、JST日付付きのCSVとしてダウンロードする。 */
  downloadAccessLog(): void {
    if (this.isExporting) {
      return
    }

    this.validationError = validateDateRange(this.startDate, this.endDate)
    if (this.validationError) {
      return
    }

    this.isExporting = true
    this.errorMessage = ''
    this.trygroupDashboardSettingsService
      .exportAccessLog(this.startDate, this.endDate)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (rows) => {
          this.isExporting = false
          const [headers, ...data] = rows
          if (data.length === 0) {
            this.errorMessage = 'エクスポート対象データがありません。'
            return
          }

          this.csvExportService.downloadCsv(
            `access_log_export_${formatJstYyyymmdd()}.csv`,
            buildCsvHeaders(headers),
            buildCsvRows(headers, data),
          )
        },
        error: () => {
          this.isExporting = false
          this.errorMessage = 'アクセスログCSVのエクスポートに失敗しました。'
        },
      })
  }
}

function validateDateRange(startDate: string, endDate: string): string {
  const start = new Date(startDate)
  const end = new Date(endDate)
  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return '開始日と終了日を入力してください。'
  }
  const diffDays = Math.floor(
    (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (diffDays < 0) {
    return '終了日は開始日以降の日付を指定してください。'
  }
  if (diffDays > MAX_EXPORT_DAYS) {
    return `期間は${MAX_EXPORT_DAYS}日以内で指定してください。`
  }
  return ''
}

/** JST基準のYYYY-MM-DD文字列を返す（date picker 初期値用）。 */
function formatDateInput(date: Date): string {
  const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
  return jstDate.toISOString().slice(0, 10)
}

/** 2D配列の先頭行を CsvExportService のヘッダー定義へ変換する。 */
function buildCsvHeaders(
  headers: string[],
): { label: string; key: keyof CsvRow }[] {
  return headers.map((label, index) => ({
    label,
    key: `column${index}`,
  }))
}

/** 2D配列のデータ行を CsvExportService の行オブジェクトへ変換する。 */
function buildCsvRows(headers: string[], rows: string[][]): CsvRow[] {
  return rows.map((row) => {
    return Object.fromEntries(
      headers.map((_, index) => [`column${index}`, row[index] ?? '']),
    ) as CsvRow
  })
}

/** JST基準のYYYYMMDD文字列を返す。 */
function formatJstYyyymmdd(date = new Date()): string {
  const jstDate = new Date(date.getTime() + 9 * 60 * 60 * 1000)
  return jstDate.toISOString().slice(0, 10).replace(/-/g, '')
}
